import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { DeleteButton, OutlinedButton } from "@/Components/v2/atoms/Button";
import { Dialog } from "@/Components/v2/atoms/Dialog/Dialog";
import { DialogActions } from "@/Components/v2/atoms/Dialog/DialogActions";
import { DialogContent } from "@/Components/v2/atoms/Dialog/DialogContent";
import { DialogContentText } from "@/Components/v2/atoms/Dialog/DialogContentText";
import { Typography } from "@/Components/v2/atoms/Typography/Typography";

import { useArchiveDocument } from "../api";
import { Document } from "../api/schema/openapi";
import { useChat } from "../hooks/useChat";

interface DeleteConfirmationProps {
  document: Document;
  handleClose: () => void;
}

export const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
  document,
  handleClose,
}) => {
  const archiveDocument = useArchiveDocument();
  const { setEnabledDocumentIds } = useChat();

  const handleDelete = async () => {
    try {
      await archiveDocument.mutateAsync(document.id);
      setEnabledDocumentIds((prev) => prev.filter((id) => id !== document.id));
      toast.success("Document archived");
      handleClose();
    } catch {
      toast.error("Failed to archive document");
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent sx={{ padding: "1rem" }}>
        <DialogContentText id="alert-dialog-description" padding={2}>
          <Typography variant="h4" fontWeight={500} mb={2} display="block">
            Delete {document.name}?
          </Typography>
          <Typography variant="body1" display="block">
            {document.name} will be permanently removed. This action cannot be undone, and the
            document will no longer be accessible to anyone in your organization when using Hendren.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <OutlinedButton
            variant="text"
            size="large"
            onClick={handleClose}
            sx={{ marginRight: "1.5rem" }}
            disabled={archiveDocument.isPending}
          >
            Cancel
          </OutlinedButton>
          <DeleteButton size="large" onClick={handleDelete} disabled={archiveDocument.isPending}>
            Delete
          </DeleteButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmation;
